import type { RichTextRkpiSpeed } from "@library-elements/text";
import {
  Schema,
  type DOMOutputSpec,
  type MarkSpec,
  type NodeSpec,
} from "prosemirror-model";
import {
  VARIABLE_BLOCK_ATTR,
  VARIABLE_BLOCK_CLASS,
} from "@/shared/utils/variable-block";
import {
  VARIABLE_PILL_ATTR,
  VARIABLE_PILL_CLASS,
} from "@/shared/utils/variable-pill";
import { RKPI_SPEED_OPTIONS } from "../froala/constants";
import {
  classToTextStyle,
  fontSizeValueToPx,
  fontSizeValueToStyle,
  isLegacySizeMode,
  isTextStyleId,
  textStyleClass,
  type TextStyleId,
} from "./typography";

/**
 * The schema IS the sanitizer. ProseMirror's DOMParser only keeps what a
 * parse rule below admits; every other tag, attribute, class and inline
 * style is dropped by default (spec §2.2 "never insert raw HTML", §13).
 *
 * Storage stays an HTML string in `settings.content`, so every toDOM below
 * writes the exact markup the static renderer / PDF already style
 * (`<h2>`, `<li>`, `span[data-highlight-active]`, `i[data-is-remix-icon]`…).
 */

// ─── block attributes ───────────────────────────────────────────────

const ALIGNMENTS = ["left", "center", "right", "justify"] as const;
export type Alignment = (typeof ALIGNMENTS)[number];
export type Direction = "rtl" | null;
export type TextAnimation = { speed: string; baseOpacity: string } | null;

/**
 * Attributes every block (paragraph, heading, list item) carries. Each one
 * maps to exactly one inline declaration or data attribute the Storydoc
 * renderer already understands — nothing else survives.
 */
export const blockAttrs = {
  align: { default: null as Alignment | null },
  // Toolbar line-height values are unitless ("1", "1.15", "1.5", "2").
  lineHeight: { default: null as string | null },
  direction: { default: null as Direction },
  // Text animation is a block feature (data-text-animation-* + opacity).
  animation: { default: null as TextAnimation },
};

export type BlockAttrs = {
  align: Alignment | null;
  lineHeight: string | null;
  direction: Direction;
  animation: TextAnimation;
};

const readAlign = (dom: HTMLElement): Alignment | null => {
  const value = (dom.style.textAlign || dom.getAttribute("align") || "")
    .trim()
    .toLowerCase();
  if (value === "start") return "left";
  if (value === "end") return "right";
  return (ALIGNMENTS as readonly string[]).includes(value)
    ? (value as Alignment)
    : null;
};

const readLineHeight = (dom: HTMLElement): string | null => {
  const value = dom.style.lineHeight.trim();
  return /^\d+(\.\d+)?$/.test(value) ? value : null;
};

export const readBlockAttrs = (dom: HTMLElement): BlockAttrs => ({
  align: readAlign(dom),
  lineHeight: readLineHeight(dom),
  direction: dom.style.direction.trim().toLowerCase() === "rtl" ? "rtl" : null,
  animation: dom.hasAttribute("data-text-animation-active")
    ? {
        speed: dom.getAttribute("data-text-animation-speed") ?? "medium",
        baseOpacity:
          dom.getAttribute("data-text-animation-base-opacity") ?? "0.4",
      }
    : null,
});

/** Serialize block attributes to the `style` + data attributes Froala wrote. */
export const blockDomAttrs = (attrs: BlockAttrs): Record<string, string> => {
  const out: Record<string, string> = {};
  const style: string[] = [];
  if (attrs.align) style.push(`text-align: ${attrs.align};`);
  if (attrs.lineHeight) style.push(`line-height: ${attrs.lineHeight};`);
  if (attrs.direction) style.push(`direction: ${attrs.direction};`);
  if (attrs.animation) {
    out["data-text-animation-active"] = "true";
    out["data-text-animation-speed"] = attrs.animation.speed;
    out["data-text-animation-base-opacity"] = attrs.animation.baseOpacity;
    style.push(`opacity: ${attrs.animation.baseOpacity};`);
  }
  if (style.length) out.style = style.join(" ");
  return out;
};

const appendStyle = (attrs: Record<string, string>, decl: string) => {
  attrs.style = [attrs.style, decl].filter(Boolean).join(" ");
};

/**
 * Size every character of the block shares, written on the block itself: an
 * inline span can't shrink the line box below the block's own font size, so a
 * 40px <h2> with 12px text would keep its 40px height. Derived from the
 * font_size marks (applyDerivedAttrs), never parsed.
 */
const derivedFontSize = { fontSize: { default: null as number | null } };

const textblockDomAttrs = (attrs: Record<string, unknown>) => {
  const out = blockDomAttrs(attrs as BlockAttrs);
  if (attrs.fontSize)
    appendStyle(out, `font-size: ${attrs.fontSize as number}px;`);
  return out;
};

const el = (dom: HTMLElement | string): HTMLElement | null =>
  typeof dom === "string" ? null : dom;

const getBlockAttrs = (dom: HTMLElement | string) => {
  const node = el(dom);
  return node ? readBlockAttrs(node) : {};
};

// ─── nodes ───────────────────────────────────────────────────────────

export type ListKind = "bullet" | "checkmark" | "icons";

/** Class on every ProseMirror-written <ul>/<ol>; the static stylesheet keys its list rendering on it. */
export const LIST_CLASS = "sd-list";

export type RkpiAttrs = {
  to: number;
  from: number;
  prefix: string;
  suffix: string;
  speed: RichTextRkpiSpeed;
  decimal: boolean;
};

const readRkpi = (dom: HTMLElement): RkpiAttrs => {
  const num = (name: string, fallback: number) => {
    const value = Number(dom.getAttribute(name));
    return Number.isNaN(value) || dom.getAttribute(name) == null
      ? fallback
      : value;
  };
  const rawSpeed = dom.getAttribute("data-rkpi-speed") ?? "medium";
  const speed = (
    rawSpeed in RKPI_SPEED_OPTIONS ? rawSpeed : "medium"
  ) as RichTextRkpiSpeed;
  return {
    to: num("data-rkpi-number-to", 100),
    from: num("data-rkpi-number-from", 0),
    prefix: dom.getAttribute("data-rkpi-prefix") ?? "",
    suffix: dom.getAttribute("data-rkpi-suffix") ?? "",
    speed,
    decimal: dom.getAttribute("data-rkpi-decimal") === "true",
  };
};

/** Display text of a counter at its end value (what readers see after the animation). */
export const rkpiText = (attrs: RkpiAttrs): string => {
  const value = attrs.decimal
    ? String(attrs.to)
    : String(attrs.to).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${attrs.prefix}${value}${attrs.suffix}`;
};

const nodes: Record<string, NodeSpec> = {
  doc: { content: "block+" },

  paragraph: {
    content: "inline*",
    group: "block",
    attrs: { ...blockAttrs, ...derivedFontSize },
    parseDOM: [
      { tag: "p", getAttrs: getBlockAttrs },
      // Outside the 4-style hierarchy → normal text (Froala did the same).
      { tag: "h5", getAttrs: getBlockAttrs },
      { tag: "h6", getAttrs: getBlockAttrs },
    ],
    toDOM: (node) => ["p", textblockDomAttrs(node.attrs), 0],
  },

  heading: {
    content: "inline*",
    group: "block",
    defining: true,
    attrs: { level: { default: 2 }, ...blockAttrs, ...derivedFontSize },
    parseDOM: [1, 2, 3, 4].map((level) => ({
      tag: `h${level}`,
      getAttrs: (dom: HTMLElement | string) => ({
        level,
        ...getBlockAttrs(dom),
      }),
    })),
    toDOM: (node) => [`h${node.attrs.level}`, textblockDomAttrs(node.attrs), 0],
  },

  bullet_list: {
    content: "list_item+",
    group: "block",
    attrs: {
      kind: { default: "bullet" as ListKind },
      // Icon lists: the remix/font-awesome class name, e.g. "ri-check-fill".
      iconName: { default: null as string | null },
    },
    parseDOM: [
      {
        tag: "ul",
        getAttrs: (dom) => {
          const node = el(dom);
          if (!node) return {};
          if (node.classList.contains("custom-check-mark")) {
            return { kind: "checkmark" };
          }
          if (node.classList.contains("custom-icon-mark")) {
            const iconName =
              node.getAttribute("data-icon-name") ||
              Array.from(node.classList).find(
                (c) => c.startsWith("ri-") || c.startsWith("fa-"),
              ) ||
              null;
            return { kind: "icons", iconName };
          }
          return { kind: "bullet" };
        },
      },
    ],
    toDOM: (node) => {
      const { kind, iconName } = node.attrs as {
        kind: ListKind;
        iconName: string | null;
      };
      // `sd-list` marks a ProseMirror-authored list so the shared stylesheet
      // renders the spec §4 marker column only for these (Froala lists keep
      // the legacy native markers).
      const classes = [LIST_CLASS];
      if (kind === "checkmark") classes.push("custom-check-mark");
      if (kind === "icons") classes.push("custom-icon-mark", iconName ?? "");
      const attrs: Record<string, string> = {
        class: classes.filter(Boolean).join(" "),
      };
      if (kind === "icons" && iconName) attrs["data-icon-name"] = iconName;
      return ["ul", attrs, 0];
    },
  },

  ordered_list: {
    content: "list_item+",
    group: "block",
    parseDOM: [{ tag: "ol" }],
    toDOM: () => ["ol", { class: LIST_CLASS }, 0],
  },

  list_item: {
    content: "paragraph block*",
    defining: true,
    attrs: {
      // Whole-item typography (spec §3.7). Mixed styles inside the item are
      // inline `text_style` marks on top of this.
      textStyle: { default: null as TextStyleId | null },
      ...blockAttrs,
      // Icon lists store the glyph per item (Froala shape: data-icon).
      icon: { default: null as string | null },
      iconName: { default: null as string | null },
      // Colour of the item's first word, so the marker matches the text next
      // to it. Derived from the text (applyDerivedAttrs), never parsed.
      markerColor: { default: null as string | null },
    },
    parseDOM: [
      {
        tag: "li",
        getAttrs: (dom) => {
          const node = el(dom);
          if (!node) return {};
          return {
            textStyle:
              classToTextStyle(node.getAttribute("class")) ??
              fontSizeValueToStyle(node.style.fontSize) ??
              null,
            ...readBlockAttrs(node),
            icon: node.getAttribute("data-icon"),
            iconName: node.getAttribute("data-icon-name"),
          };
        },
      },
    ],
    toDOM: (node) => {
      const { textStyle, icon, iconName } = node.attrs as {
        textStyle: TextStyleId | null;
        icon: string | null;
        iconName: string | null;
      };
      const attrs: Record<string, string> = {};
      if (textStyle && textStyle !== "normal") {
        attrs.class = textStyleClass(textStyle);
      }
      Object.assign(attrs, blockDomAttrs(node.attrs as BlockAttrs));
      // A custom property, not `color`: the li's colour would cascade into
      // nested items' text, the variable only reaches its own marker.
      const markerColor = node.attrs.markerColor as string | null;
      if (markerColor) appendStyle(attrs, `--sd-marker-color: ${markerColor};`);
      if (icon) attrs["data-icon"] = icon;
      if (iconName) attrs["data-icon-name"] = iconName;
      return ["li", attrs, 0];
    },
  },

  text: { group: "inline" },

  hard_break: {
    inline: true,
    group: "inline",
    selectable: false,
    parseDOM: [{ tag: "br" }],
    toDOM: () => ["br"],
  },

  /**
   * Icons are text characters (spec §5): an atom that inherits font size and
   * line height from its run. No size attribute exists, so nothing can make
   * it behave like an image.
   */
  icon: {
    inline: true,
    group: "inline",
    atom: true,
    draggable: false,
    // Atoms take run formatting (bold, colour, link…) but never a size (§5).
    // text_style: a copied counter keeps the size of the block it came from (QA G2).
    marks:
      "text_style strong em underline strike link color highlight ai_classification",
    attrs: {
      family: { default: "remix" as "remix" | "fa" },
      unicode: { default: "" },
    },
    parseDOM: [
      {
        tag: "i[data-is-remix-icon]",
        getAttrs: (dom) => ({
          family: "remix",
          unicode: el(dom)?.getAttribute("data-remix-unicode") ?? "",
        }),
      },
      {
        tag: "i[data-is-font-awesome-icon]",
        getAttrs: (dom) => ({
          family: "fa",
          unicode: el(dom)?.getAttribute("data-font-awesome-unicode") ?? "",
        }),
      },
    ],
    toDOM: (node) => {
      const { family, unicode } = node.attrs as {
        family: "remix" | "fa";
        unicode: string;
      };
      const attrs: Record<string, string> = { contenteditable: "false" };
      if (family === "fa") {
        attrs["data-is-font-awesome-icon"] = "true";
        attrs["data-font-awesome-unicode"] = unicode;
      } else {
        attrs["data-is-remix-icon"] = "true";
        attrs["data-remix-unicode"] = unicode;
      }
      const code = Number.parseInt(unicode, 16);
      const glyph = Number.isNaN(code) ? "" : String.fromCharCode(code);
      return ["i", attrs, ["span", glyph]];
    },
  },

  /**
   * Dynamic variable `{{name}}`. Stored as the plain token (serializeHtml
   * turns the pill back into text, exactly like Froala's save path); in the
   * editor it's a non-editable atom, so the caret can't enter it and a click,
   * arrow key or Backspace treats it as one unit — ProseMirror's own atom
   * handling, no caret-guard workarounds. Raw tokens become pills through
   * applyVariableTokens (load, paste, typing, inserts). Marks apply to it, so
   * a bold or coloured pill stores as `<strong>{{name}}</strong>`.
   */
  variable: {
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: true,
    attrs: { name: {} },
    // Copy as text keeps the token (plain-text clipboard, doc.textContent).
    leafText: (node) => `{{${node.attrs.name as string}}}`,
    parseDOM: [
      {
        // A copied pill (another editor, the inactive render) keeps its name;
        // the label inside — a resolved value — is never read.
        tag: `span.${VARIABLE_PILL_CLASS}[${VARIABLE_PILL_ATTR}]`,
        getAttrs: (dom) => {
          const name = el(dom)?.getAttribute(VARIABLE_PILL_ATTR);
          return name ? { name } : false;
        },
      },
    ],
    toDOM: (node) => [
      "span",
      {
        class: VARIABLE_PILL_CLASS,
        [VARIABLE_PILL_ATTR]: node.attrs.name as string,
        contenteditable: "false",
      },
      `{{${node.attrs.name as string}}}`,
    ],
  },

  /**
   * `{{{name}}}` in VERSION mode: a read-only box showing the variable's HTML
   * value (Froala and the inactive render do the same). Authoring keeps the
   * token as plain text, so this node only exists in a version editor
   * (applyVariableBlocks). Stored as the token (serializeHtml). Inline, like
   * Froala's: the box sits where the token was, inside its paragraph.
   */
  variable_block: {
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    attrs: { name: {} },
    leafText: (node) => `{{{${node.attrs.name as string}}}}`,
    parseDOM: [
      {
        tag: `div.${VARIABLE_BLOCK_CLASS}[${VARIABLE_BLOCK_ATTR}]`,
        getAttrs: (dom) => {
          const name = el(dom)?.getAttribute(VARIABLE_BLOCK_ATTR);
          return name ? { name } : false;
        },
      },
    ],
    toDOM: (node) => [
      "div",
      {
        class: VARIABLE_BLOCK_CLASS,
        [VARIABLE_BLOCK_ATTR]: node.attrs.name as string,
        contenteditable: "false",
      },
    ],
  },

  emoji: {
    inline: true,
    group: "inline",
    atom: true,
    draggable: false,
    // text_style: a copied counter keeps the size of the block it came from (QA G2).
    marks:
      "text_style strong em underline strike link color highlight ai_classification",
    attrs: { shortcodes: { default: "" } },
    parseDOM: [
      {
        tag: "em-emoji[shortcodes]",
        getAttrs: (dom) => ({
          shortcodes: el(dom)?.getAttribute("shortcodes") ?? "",
        }),
      },
    ],
    toDOM: (node) => [
      "em-emoji",
      { shortcodes: node.attrs.shortcodes as string, contenteditable: "false" },
    ],
  },

  /**
   * Animated KPI counter (the toolbar's "Counter"). Stored as
   * `span[data-rkpi-*]`; the editor shows the end value, storage rewrites the
   * text to the start value with `data-rkpi-active="false"` so the reader
   * animates it (see html.ts) — same as Froala's save path.
   */
  rkpi: {
    inline: true,
    group: "inline",
    atom: true,
    draggable: false,
    // text_style: a copied counter keeps the size of the block it came from (QA G2).
    marks:
      "text_style strong em underline strike link color highlight ai_classification",
    attrs: {
      to: { default: 100 },
      from: { default: 0 },
      prefix: { default: "" },
      suffix: { default: "" },
      speed: { default: "medium" as RichTextRkpiSpeed },
      decimal: { default: false },
    },
    parseDOM: [
      {
        tag: "span[data-rkpi-active]",
        getAttrs: (dom) => {
          const node = el(dom);
          return node ? readRkpi(node) : {};
        },
      },
    ],
    toDOM: (node) => {
      const attrs = node.attrs as RkpiAttrs;
      return [
        "span",
        {
          contenteditable: "false",
          "data-rkpi-active": "true",
          "data-rkpi-number-to": String(attrs.to),
          "data-rkpi-number-from": String(attrs.from),
          "data-rkpi-prefix": attrs.prefix,
          "data-rkpi-suffix": attrs.suffix,
          "data-rkpi-speed": attrs.speed,
          "data-rkpi-decimal": String(attrs.decimal),
        },
        rkpiText(attrs),
      ];
    },
    // Copy as text keeps the number the reader sees (QA G1).
    leafText: (node) => rkpiText(node.attrs as RkpiAttrs),
  },
};

// ─── marks ───────────────────────────────────────────────────────────

const isBoldWeight = (value: string) =>
  /^(bold(er)?|[5-9]\d{2,})$/.test(value.trim());

const SAFE_HREF = /^(https?:|mailto:|tel:|#|\/)/i;
const ANCHOR_HREF = "javascript:void(0);";

const isUnsafeHref = (href: string) => {
  try {
    const normalized = decodeURIComponent(href)
      .replace(/[\s\0]+/g, "")
      .toLowerCase();
    return (
      normalized.startsWith("javascript:") || normalized.startsWith("vbscript:")
    );
  } catch {
    return href
      .replace(/[\s\0]+/g, "")
      .toLowerCase()
      .startsWith("javascript:");
  }
};

const marks: Record<string, MarkSpec> = {
  // First = outermost. A style segment (span.sd-display) sets its own colour
  // and size from the design system, so every mark that can override those —
  // colour, links, highlights, an explicit size — must sit INSIDE it, or the
  // segment's class wins and e.g. a colour never reaches a Display word (QA C8).
  text_style: {
    attrs: { style: {} },
    parseDOM: [
      {
        // sd-display / sd-title / sd-heading / sd-subheading / sd-normal
        // (and the earlier sd-text-* spelling); other sd-* spans fall through.
        tag: "span[class*='sd-']",
        getAttrs: (dom) => {
          const style = classToTextStyle(
            el(dom)?.getAttribute("class") ?? null,
          );
          return style ? { style } : false;
        },
      },
      {
        style: "font-size",
        getAttrs: (value) => {
          const style = fontSizeValueToStyle(value as string);
          return style ? { style } : false;
        },
      },
    ],
    toDOM: (mark) => {
      const style = mark.attrs.style as string;
      return [
        "span",
        {
          class: isTextStyleId(style)
            ? textStyleClass(style)
            : textStyleClass("normal"),
        },
        0,
      ] as DOMOutputSpec;
    },
  },
  strong: {
    parseDOM: [
      { tag: "strong" },
      // Google Docs wraps everything in <b style="font-weight:normal">.
      {
        tag: "b",
        getAttrs: (dom) => {
          const weight = el(dom)?.style.fontWeight ?? "";
          return weight && !isBoldWeight(weight) ? false : null;
        },
      },
      {
        style: "font-weight",
        getAttrs: (value) => (isBoldWeight(value as string) ? null : false),
      },
    ],
    toDOM: () => ["strong", 0],
  },

  em: {
    parseDOM: [
      { tag: "em" },
      {
        tag: "i:not([data-is-remix-icon]):not([data-is-font-awesome-icon])",
      },
      { style: "font-style=italic" },
    ],
    toDOM: () => ["em", 0],
  },

  underline: {
    parseDOM: [
      { tag: "u" },
      {
        style: "text-decoration",
        getAttrs: (value) =>
          (value as string).includes("underline") ? null : false,
      },
      {
        style: "text-decoration-line",
        getAttrs: (value) =>
          (value as string).includes("underline") ? null : false,
      },
    ],
    toDOM: () => ["u", 0],
  },

  strike: {
    parseDOM: [
      { tag: "s" },
      { tag: "strike" },
      { tag: "del" },
      {
        style: "text-decoration",
        getAttrs: (value) =>
          (value as string).includes("line-through") ? null : false,
      },
      {
        style: "text-decoration-line",
        getAttrs: (value) =>
          (value as string).includes("line-through") ? null : false,
      },
    ],
    toDOM: () => ["s", 0],
  },

  subscript: {
    excludes: "superscript",
    parseDOM: [{ tag: "sub" }, { style: "vertical-align=sub" }],
    toDOM: () => ["sub", 0],
  },

  superscript: {
    excludes: "subscript",
    parseDOM: [{ tag: "sup" }, { style: "vertical-align=super" }],
    toDOM: () => ["sup", 0],
  },

  link: {
    attrs: {
      href: {},
      target: { default: "_blank" as string | null },
      // Storydoc link options (same data-* the static renderer reads).
      openInPopup: { default: null as boolean | null },
      event: { default: null as string | null },
      anchor: { default: null as string | null },
    },
    inclusive: false,
    parseDOM: [
      {
        tag: "a[href]",
        getAttrs: (dom) => {
          const node = el(dom);
          if (!node) return false;
          const href = node.getAttribute("href") ?? "";
          const anchor = node.getAttribute("data-anchor-trigger");
          if (anchor) {
            return { href: ANCHOR_HREF, target: null, anchor };
          }
          if (
            isUnsafeHref(href) ||
            !(SAFE_HREF.test(href) || /^[\w.-]+\.[a-z]{2,}/i.test(href))
          ) {
            return false;
          }
          const popup = node.getAttribute("data-open-in-popup");
          return {
            href,
            target: node.getAttribute("target") ?? "_blank",
            openInPopup: popup == null ? null : popup === "true",
            event: node.getAttribute("data-analytics-click-event"),
          };
        },
      },
    ],
    toDOM: (mark) => {
      const { href, target, openInPopup, event, anchor } = mark.attrs as {
        href: string;
        target: string | null;
        openInPopup: boolean | null;
        event: string | null;
        anchor: string | null;
      };
      const attrs: Record<string, string> = { href };
      if (anchor) {
        attrs["data-anchor-trigger"] = anchor;
      } else {
        if (target) attrs.target = target;
        attrs.rel = "noopener noreferrer";
        if (openInPopup != null)
          attrs["data-open-in-popup"] = String(openInPopup);
        if (event) attrs["data-analytics-click-event"] = event;
      }
      return ["a", attrs, 0];
    },
  },

  /**
   * Explicit font size from the toolbar (px). Applies like colour — to the
   * text, never to the block — so an H1 at 12px stays an H1. Read from
   * existing content on load; pasted sizes are mapped to a style instead
   * (the text_style rule below takes them in paste mode).
   */
  font_size: {
    attrs: { px: {} },
    parseDOM: [
      {
        style: "font-size",
        getAttrs: (value) => {
          if (!isLegacySizeMode()) return false;
          const px = fontSizeValueToPx(value as string);
          return px ? { px } : false;
        },
      },
    ],
    toDOM: (mark) => [
      "span",
      { style: `font-size: ${mark.attrs.px as number}px;` },
      0,
    ],
  },

  color: {
    attrs: { color: {} },
    parseDOM: [
      {
        style: "color",
        getAttrs: (value) => {
          const color = (value as string).trim();
          if (!color || color === "inherit" || color === "initial")
            return false;
          return { color };
        },
      },
    ],
    toDOM: (mark) => [
      "span",
      { style: `color: ${mark.attrs.color as string};` },
      0,
    ],
  },

  /**
   * Highlights behave like text (spec §7): an inline span whose only box
   * properties are horizontal padding/margin, so line height and wrapping are
   * unaffected. The editor renders the *active* state so the author sees the
   * colour; storage flips `data-highlight-active` to "false" so the reveal
   * animation plays for readers (see html.ts).
   */
  highlight: {
    attrs: {
      bgColor: { default: "var(--highlight-background-color)" },
      textColor: { default: "var(--highlight-text-color)" },
      animation: { default: "anim-bg" },
      static: { default: false },
    },
    parseDOM: [
      {
        tag: "span[data-highlight-active]",
        getAttrs: (dom) => {
          const node = el(dom);
          if (!node) return false;
          return {
            bgColor:
              node.getAttribute("data-highlight-bg-color") ||
              "var(--highlight-background-color)",
            textColor:
              node.getAttribute("data-highlight-text-color") ||
              "var(--highlight-text-color)",
            animation:
              node.getAttribute("data-highlight-animation") || "anim-bg",
            static: node.getAttribute("data-highlight-static") === "true",
          };
        },
      },
    ],
    toDOM: (mark) => {
      const { bgColor, textColor, animation } = mark.attrs as {
        bgColor: string;
        textColor: string;
        animation: string;
        static: boolean;
      };
      return [
        "span",
        {
          "data-highlight-active": "true",
          "data-highlight-animation": animation,
          "data-highlight-bg-color": bgColor,
          "data-highlight-text-color": textColor,
          "data-highlight-static": String(mark.attrs.static as boolean),
          style: `--var-highlight-bg-color: ${bgColor};--var-highlight-text-color: ${textColor};`,
        },
        0,
      ];
    },
  },

  /** Admin-only AI classification tag on a text range. */
  ai_classification: {
    attrs: { value: {} },
    parseDOM: [
      {
        tag: "span[data-ai-classification]",
        getAttrs: (dom) => {
          const value = el(dom)?.getAttribute("data-ai-classification");
          return value ? { value } : false;
        },
      },
    ],
    toDOM: (mark) => [
      "span",
      { "data-ai-classification": mark.attrs.value as string },
      0,
    ],
  },

  /**
   * Inline typography (spec §3.2–3.4): one of the five styles stored per text
   * segment as a controlled wrapper `<span class="sd-text-…">`. Raw font sizes
   * on input are mapped through §3.5 and never stored.
   */
};

export const textSchema = new Schema({ nodes, marks });

export type TextSchema = typeof textSchema;
